import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Sparkles, Brain, Clock, Shield, BookMarked } from 'lucide-react';

// "What brings you here" options
export const REASONS = [
  { value: '', label: 'What brings you here? (optional)' },
  { value: 'breakup', label: 'Healing from a breakup' },
  { value: 'divorce', label: 'Going through a divorce' },
  { value: 'loss', label: 'Processing loss or grief' },
  { value: 'codependency', label: 'Breaking codependent patterns' },
  { value: 'self_worth', label: 'Rebuilding self-worth' },
  { value: 'anxiety', label: 'Managing anxiety and overwhelm' },
  { value: 'boundaries', label: 'Learning to set boundaries' },
  { value: 'trust', label: 'Learning to trust again' },
  { value: 'other', label: 'Something else' },
];

const MEMORY_CARDS = [
  { Icon: Brain, color: '#C9A84C', bg: 'rgba(201,168,76,0.08)', border: 'rgba(201,168,76,0.1)', text: 'Remembers your patterns' },
  { Icon: Clock, color: '#A9756B', bg: 'rgba(232,180,184,0.08)', border: 'rgba(232,180,184,0.1)', text: 'Tracks your milestones' },
  { Icon: Shield, color: '#7d9d8c', bg: 'rgba(125,157,140,0.08)', border: 'rgba(125,157,140,0.1)', text: 'Knows your strengths' },
  { Icon: BookMarked, color: '#C9A84C', bg: 'rgba(201,168,76,0.08)', border: 'rgba(201,168,76,0.1)', text: 'Carries your story' },
];

const PERKS = [
  '30 days completely free',
  'AI companion with continuous memory',
  'Direct founder access for feedback',
  'Founding member pricing locked forever',
];

export const BetaBackground = () => (
  <>
    <div
      className="fixed inset-0 pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.06) 0%, transparent 60%)',
        animation: 'breathe 8s ease-in-out infinite',
      }}
    />
    <div
      className="fixed inset-0 pointer-events-none opacity-[0.03]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }}
    />
    <style>{`
      @keyframes breathe {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.02); opacity: 0.95; }
      }
      .memory-card-beta.animate-in {
        opacity: 1;
        transform: translateY(0);
      }
    `}</style>
  </>
);

export const BetaHero = () => (
  <>
    <div className="text-center mb-8">
      <span
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs tracking-[0.15em]"
        style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}
      >
        <Sparkles className="w-3 h-3" />
        NOW ACCEPTING BETA TESTERS
      </span>
    </div>
    <h1
      className="text-3xl md:text-4xl lg:text-5xl text-center mb-6 leading-tight"
      style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
    >
      Your healing deserves a companion who remembers.
    </h1>
    <p className="text-center text-lg mb-10" style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}>
      A trauma-informed AI sanctuary for women healing from heartbreak and loss.
    </p>
  </>
);

export const BetaPerks = () => (
  <div className="p-6 rounded-2xl mb-8" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
    <p className="text-sm mb-4" style={{ color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}>
      What beta testers receive:
    </p>
    <ul className="space-y-3">
      {PERKS.map((item) => (
        <li key={item} className="flex items-center gap-3 text-sm" style={{ color: '#2E2E42', fontFamily: 'Jost, sans-serif' }}>
          <span style={{ color: '#C9A84C' }}>✦</span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export const MemoryDifferentiator = () => (
  <div className="mb-8">
    <p className="text-center text-xs tracking-[0.15em] uppercase mb-6" style={{ color: '#8A8798' }}>
      What makes Sage different
    </p>
    <div className="grid grid-cols-2 gap-3">
      {MEMORY_CARDS.map(({ Icon, color, bg, border, text }, i) => (
        <div
          key={text}
          className="memory-card-beta opacity-0 translate-y-4 transition-all duration-500 ease-out p-4 rounded-xl text-center"
          style={{ background: bg, border: `1px solid ${border}`, transitionDelay: `${i * 100}ms` }}
        >
          <Icon className="w-5 h-5 mx-auto mb-2" style={{ color }} strokeWidth={1.5} />
          <p className="text-xs" style={{ color: '#4A4A5A' }}>{text}</p>
        </div>
      ))}
    </div>
    <p className="text-center text-xs italic mt-4" style={{ color: '#8A8798', fontFamily: 'Playfair Display, serif' }}>
      Most apps forget you. Sage remembers.
    </p>
  </div>
);

export const BetaForm = ({ firstName, setFirstName, email, setEmail, reason, setReason, loading, onSubmit }) => {
  const inputStyle = {
    backgroundColor: '#F0EBE4',
    color: '#1A1A2E',
    fontFamily: 'Jost, sans-serif'
  };
  return (
    <>
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="w-full rounded-xl py-6 px-5 text-base border-0"
          style={inputStyle}
          data-testid="beta-first-name"
        />
        <Input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl py-6 px-5 text-base border-0"
          style={inputStyle}
          data-testid="beta-email"
        />
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-xl py-4 px-5 text-base border-0 appearance-none cursor-pointer"
          style={{ ...inputStyle, color: reason ? '#1A1A2E' : '#6B6878' }}
          data-testid="beta-reason"
        >
          {REASONS.map((r) => (
            <option key={r.value} value={r.value} style={{ backgroundColor: '#FFFFFF' }}>
              {r.label}
            </option>
          ))}
        </select>
        <Button
          type="submit"
          disabled={loading}
          className="w-full rounded-full py-6 text-base font-medium transition-all duration-300 hover:scale-[1.02]"
          style={{ backgroundColor: '#C9A84C', color: '#FBF9F6', fontFamily: 'Jost, sans-serif' }}
          data-testid="beta-submit"
        >
          {loading ? 'Reserving your spot...' : 'Reserve My Spot in the Sanctuary'}
        </Button>
      </form>

      <p className="text-center text-xs mt-6" style={{ color: '#8A8798', fontFamily: 'Jost, sans-serif' }}>
        No credit card required. Your information is private and never shared.
      </p>

      <div className="text-center mt-8 pt-8 border-t" style={{ borderColor: '#EAE4DC' }}>
        <p className="text-xs" style={{ color: '#8A8798', fontFamily: 'Jost, sans-serif' }}>
          Founding member pricing: <span style={{ color: '#C9A84C' }}>$27/month</span> locked forever
        </p>
        <p className="text-xs mt-1" style={{ color: '#A3A0AE', fontFamily: 'Jost, sans-serif' }}>
          Public launch price: $37/month
        </p>
      </div>
    </>
  );
};

export const BetaSuccess = ({ firstName }) => (
  <div className="text-center py-12">
    <div
      className="w-20 h-20 rounded-full mx-auto mb-8 flex items-center justify-center"
      style={{ background: 'rgba(201,168,76,0.15)', boxShadow: '0 0 40px rgba(201,168,76,0.2)' }}
    >
      <Check className="w-10 h-10" style={{ color: '#C9A84C' }} />
    </div>
    <h2 className="text-3xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
      You're on the list, {firstName}.
    </h2>
    <p className="text-lg mb-8" style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}>
      Check your inbox for what's next.
    </p>
    <div className="p-6 rounded-2xl inline-block" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
      <p className="text-sm" style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}>
        Your founding member pricing:
      </p>
      <p className="text-2xl mt-2" style={{ color: '#C9A84C', fontFamily: 'Playfair Display, serif' }}>
        $27/month forever
      </p>
    </div>
  </div>
);
